import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';

/**
 * HISP Connection Service
 * Handles CRUD operations for HISP connections via backend API
 */

/**
 * Get all HISP connections with optional filtering
 * @param {Object} filters - Filters (facilityId, active)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} HISP connections with pagination
 */
export async function getHispConnections(filters = {}, page = 1, limit = 50) {
  try {
    console.log('Fetching HISP connections with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/hispConnections/hisp-connections?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      connections: response.data.connections,
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching HISP connections:', error);
    throw error;
  }
}

/**
 * Get a specific HISP connection by ID
 * @param {string} connectionId - HISP connection ID
 * @returns {Promise<Object>} HISP connection object
 */
export async function getHispConnectionById(connectionId) {
  try {
    console.log('Fetching HISP connection by ID:', connectionId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/hispConnections/hisp-connections/${connectionId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.connection;
  } catch (error) {
    console.error('Error fetching HISP connection by ID:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new HISP connection
 * @param {Object} connectionData - HISP connection data
 * @returns {Promise<Object>} Created HISP connection
 */
export async function createHispConnection(connectionData) {
  try {
    console.log('Creating HISP connection:', connectionData.name);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/hispConnections/hisp-connections`,
      connectionData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating HISP connection:', error);
    throw error;
  }
}

/**
 * Update an existing HISP connection
 * @param {string} connectionId - HISP connection ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export async function updateHispConnection(connectionId, updateData) {
  try {
    console.log('Updating HISP connection:', connectionId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/hispConnections/hisp-connections/${connectionId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating HISP connection:', error);
    throw error;
  }
}

/**
 * Delete a HISP connection
 * @param {string} connectionId - HISP connection ID
 * @param {boolean} permanent - Whether to permanently delete
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteHispConnection(connectionId, permanent = false) {
  try {
    console.log('Deleting HISP connection:', connectionId);
    const token = await getAuthToken();
    
    const params = permanent ? '?permanent=true' : '';
    
    const response = await axios.delete(
      `${API_BASE}/hispConnections/hisp-connections/${connectionId}${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting HISP connection:', error);
    throw error;
  }
}

/**
 * Test a HISP connection
 * @param {string} connectionId - HISP connection ID
 * @returns {Promise<Object>} Test result
 */
export async function testHispConnection(connectionId) {
  try {
    console.log('Testing HISP connection:', connectionId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/hispConnections/hisp-connections/${connectionId}/test`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testResult;
  } catch (error) {
    console.error('Error testing HISP connection:', error);
    throw error;
  }
}

/**
 * Get HISP connections for a specific facility
 * @param {string} facilityId - Facility ID
 * @param {boolean} activeOnly - Whether to include only active connections
 * @returns {Promise<Array>} Array of HISP connections
 */
export async function getHispConnectionsForFacility(facilityId, activeOnly = true) {
  try {
    const filters = {
      facilityId,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getHispConnections(filters, 1, 100);
    return result.connections;
  } catch (error) {
    console.error('Error fetching HISP connections for facility:', error);
    throw error;
  }
}

/**
 * Get all facilities that already have HISP connections assigned
 * Used to determine which facilities are unavailable for assignment
 * @param {string} excludeConnectionId - Optional connection ID to exclude (for edits)
 * @returns {Promise<Array>} Array of { facilityId, connectionId, connectionName }
 */
export async function getFacilitiesWithHispConnections(excludeConnectionId = null) {
  try {
    console.log('Fetching facilities with existing HISP connections');
    const token = await getAuthToken();
    
    const params = excludeConnectionId 
      ? `?excludeConnectionId=${excludeConnectionId}` 
      : '';
    
    const response = await axios.get(
      `${API_BASE}/hispConnections/hisp-connections/facilities/assigned${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.assignedFacilities || [];
  } catch (error) {
    console.error('Error fetching facilities with HISP connections:', error);
    throw error;
  }
}

const hispConnectionService = {
  getHispConnections,
  getHispConnectionById,
  createHispConnection,
  updateHispConnection,
  deleteHispConnection,
  testHispConnection,
  getHispConnectionsForFacility,
  getFacilitiesWithHispConnections
};

export default hispConnectionService;

